import React from "react"
import { Box, Drawer, Paper } from "@mui/material"
import { LoopHeader, type LoopHeaderProps } from "./LoopHeader"
import { LoopSidebar, type LoopSidebarProps } from "./LoopSidebar"
import { SIDEBAR_MENU_WIDTH } from "../molecules/SidebarMenu"

export type LoopShellProps = {
  header?: LoopHeaderProps
  sidebar: LoopSidebarProps
  children: React.ReactNode
}

/**
 * Shared chrome for the Loop feature: header + sidebar + main content.
 *
 * **Scroll model (single scrollport)**
 * The shell is `position: fixed; inset: 0` so it does not depend on the app
 * flex height chain. Header and sidebar stay put; **only** `.loop-shell-main`
 * scrolls. Ancestors are locked to `overflow: hidden` while mounted so they
 * cannot steal the first wheel/trackpad gesture.
 *
 * **Responsive**
 * - `md+`: permanent sidebar (collapsible rail via `sidebar.collapsed`)
 * - below `md`: temporary drawer; hamburger opens expanded menu
 */
export function LoopShell({ header, sidebar, children }: LoopShellProps) {
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false)

  const closeMobileNav = () => setMobileNavOpen(false)

  // Lock every outer scrollport while Loop is mounted. Fixed shell + this
  // guarantee only `.loop-shell-main` can scroll the page content.
  React.useEffect(() => {
    const root = document.documentElement
    const body = document.body
    const appContent = document.querySelector(".app-content")
    const prev = {
      rootOverflow: root.style.overflow,
      rootOverscroll: root.style.overscrollBehavior,
      bodyOverflow: body.style.overflow,
      bodyOverscroll: body.style.overscrollBehavior,
    }
    root.style.overflow = "hidden"
    root.style.overscrollBehavior = "none"
    body.style.overflow = "hidden"
    body.style.overscrollBehavior = "none"
    appContent?.classList.add("app-content--loop")
    return () => {
      root.style.overflow = prev.rootOverflow
      root.style.overscrollBehavior = prev.rootOverscroll
      body.style.overflow = prev.bodyOverflow
      body.style.overscrollBehavior = prev.bodyOverscroll
      appContent?.classList.remove("app-content--loop")
    }
  }, [])

  // Nested overflow:auto/scroll (incl. overflow-x) steals the first vertical
  // trackpad gesture on macOS. Retarget wheel/pixel pan to `.loop-shell-main`
  // unless the event is over an explicit allow-list (e.g. <textarea>).
  React.useEffect(() => {
    const shell = document.querySelector(".loop-shell")
    const main = document.querySelector(".loop-shell-main")
    if (!(shell instanceof HTMLElement) || !(main instanceof HTMLElement)) {
      return
    }

    const isEditable = (el: Element | null): boolean => {
      if (!el) return false
      const tag = el.tagName
      return (
        tag === "TEXTAREA" ||
        tag === "INPUT" ||
        tag === "SELECT" ||
        (el as HTMLElement).isContentEditable === true ||
        Boolean(el.closest("[data-loop-scroll]"))
      )
    }

    const isScrollContainer = (el: Element): boolean => {
      // overflow:auto/scroll creates a scrollport that can steal the first
      // vertical trackpad gesture on macOS even when content does not overflow.
      const style = window.getComputedStyle(el)
      const ox = style.overflowX
      const oy = style.overflowY
      return (
        ox === "auto" ||
        ox === "scroll" ||
        oy === "auto" ||
        oy === "scroll"
      )
    }

    const onWheel = (event: WheelEvent) => {
      if (event.ctrlKey || event.metaKey) return
      const target = event.target
      if (!(target instanceof Element)) return
      if (isEditable(target)) return
      if (!shell.contains(target)) return

      // Find a nested scrollport between target and main (not main itself).
      let nested: Element | null = null
      let el: Element | null = target
      while (el && el !== main && el !== shell) {
        if (isScrollContainer(el)) {
          nested = el
          break
        }
        el = el.parentElement
      }

      if (!nested) return

      const nestedEl = nested as HTMLElement
      const canScrollX = nestedEl.scrollWidth > nestedEl.clientWidth + 1
      const canScrollY = nestedEl.scrollHeight > nestedEl.clientHeight + 1
      // Horizontal-only strips (session date pager): keep the gesture local and
      // map vertical trackpad pans to scrollLeft so sessions stay reachable.
      if (canScrollX && !canScrollY) {
        const delta =
          Math.abs(event.deltaX) > Math.abs(event.deltaY)
            ? event.deltaX
            : event.deltaY
        if (delta !== 0) {
          event.preventDefault()
          nestedEl.scrollLeft += delta
        }
        return
      }

      // Preserve native scrolling when the nested container actually overflows
      // vertically (e.g. capped session rail, sidebar nav on short viewports).
      if (canScrollY) {
        return
      }

      // Empty overflow:auto/scroll ports still steal the first trackpad gesture —
      // retarget those vertical pans to the page scroller.
      if (Math.abs(event.deltaY) >= Math.abs(event.deltaX)) {
        event.preventDefault()
        main.scrollTop += event.deltaY
      }
    }

    shell.addEventListener("wheel", onWheel, { passive: false })
    return () => {
      shell.removeEventListener("wheel", onWheel)
    }
  }, [])

  return (
    <Box
      className="loop-shell"
      sx={{
        // Escape ancestor flex/overflow bugs — one viewport, one scroller.
        position: "fixed",
        inset: 0,
        zIndex: 10,
        width: "100%",
        height: "100%",
        maxWidth: "100%",
        maxHeight: "100%",
        minHeight: 0,
        overflow: "hidden",
        bgcolor: "vira.canvas",
        p: { xs: 0, md: 2.5 },
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          flex: "1 1 0",
          minHeight: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          width: "100%",
          maxWidth: "100%",
          borderRadius: { xs: 0, md: "6px" },
          border: "1px solid",
          borderColor: { xs: "transparent", md: "rgba(27,42,74,0.13)" },
          boxShadow: {
            xs: "none",
            md: "0px 2px 10px 0px rgba(27,42,74,0.07)",
          },
          bgcolor: "background.paper",
        }}
      >
        <LoopHeader
          {...header}
          onNavMenuClick={() => setMobileNavOpen(true)}
        />
        <Box
          sx={{
            display: "flex",
            flex: "1 1 0",
            minHeight: 0,
            height: "100%",
            overflow: "hidden",
            bgcolor: "vira.pageBg",
            maxWidth: "100%",
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              flexShrink: 0,
              minHeight: 0,
              height: "100%",
              alignSelf: "stretch",
              overflow: "hidden",
            }}
          >
            <LoopSidebar {...sidebar} />
          </Box>
          <Box
            component="main"
            className="loop-shell-main"
            sx={{
              flex: "1 1 0",
              minWidth: 0,
              minHeight: 0,
              height: "100%",
              width: "100%",
              maxWidth: "100%",
              overflowX: "hidden",
              overflowY: "auto",
              overscrollBehavior: "contain",
              WebkitOverflowScrolling: "touch",
              p: { xs: 1.5, sm: 2, md: 3 },
              bgcolor: "vira.pageBg",
              boxSizing: "border-box",
            }}
          >
            {children}
          </Box>
        </Box>
      </Paper>

      <Drawer
        anchor="left"
        open={mobileNavOpen}
        onClose={closeMobileNav}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: SIDEBAR_MENU_WIDTH.expanded,
            maxWidth: "100%",
            boxSizing: "border-box",
            bgcolor: "background.paper",
          },
        }}
      >
        <LoopSidebar
          {...sidebar}
          collapsed={false}
          onCollapsedChange={(next) => {
            sidebar.onCollapsedChange?.(next)
            if (next) {
              closeMobileNav()
            }
          }}
          onNavigate={(itemId) => {
            sidebar.onNavigate?.(itemId)
            closeMobileNav()
          }}
          onBackToClients={() => {
            sidebar.onBackToClients?.()
            closeMobileNav()
          }}
        />
      </Drawer>
    </Box>
  )
}
